/**
 * Who the legal pages say runs this site, and the two switches the owner flips (GH-812).
 *
 * ## Where the names come from
 *
 * DEC-001: every donor-visible string that names the entity routes through configuration, so a
 * rename is a config change and not a code change. The server reads `ORGANIZATION_LEGAL_NAME`,
 * `PRODUCT_NAME` and `SUPPORT_CONTACT` (`packages/config`); this static bundle has no runtime to
 * read them from, so the same three values arrive as build arguments — `VITE_ORGANIZATION_LEGAL_NAME`,
 * `VITE_PRODUCT_NAME`, `VITE_SUPPORT_CONTACT` (`infra/Dockerfile.web`, `infra/deploy.sh`).
 *
 * The fallbacks are the server's own defaults, and `site.test.ts` loads `packages/config` and
 * fails if the two ever disagree — so an unset build argument still names the same entity the
 * receipts name. The support contact has **no** fallback: a page that invents a mailbox is worse
 * than one that says it has not been configured.
 *
 * ## The date
 *
 * Counsel approved the pages 2026-10-17, so the draft banner is gone.
 * `LEGAL_LAST_UPDATED` is the date every page shows; bump it whenever the words change.
 */
import { createContext, useContext } from "react";

/** `packages/config` → `ORGANIZATION_LEGAL_NAME` default. Pinned equal by `site.test.ts`. */
export const DEFAULT_ORGANIZATION_LEGAL_NAME = "Bedier Foundation";
/** `packages/config` → `PRODUCT_NAME` default. Pinned equal by `site.test.ts`. */
export const DEFAULT_PRODUCT_NAME = "GiveHere";

/** Mailing address of the platform operator (IRS record, `docs/evidence/GH-receipt-identity.md`). */
export const PLATFORM_MAILING_ADDRESS = "2920 Hunter Rd, Weston, FL 33331-3045";

/** The operator's EIN, the donee on every receipt (`docs/evidence/GH-receipt-identity.md`, DEC-026). */
export const PLATFORM_EIN = "93-4447516";

/** ISO date the legal pages last changed. */
export const LEGAL_LAST_UPDATED = "2026-10-17";

export type SiteIdentity = {
  /** The legal entity: merchant of record, recipient of every gift, the privacy controller. */
  readonly organizationLegalName: string;
  /** What donors call the product. */
  readonly productName: string;
  /** An email address or phone number, or null when the deployment has not configured one. */
  readonly supportContact: string | null;
};

function configured(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Resolves the identity from a build environment. Pure, so the fallbacks can be tested. */
export function siteIdentityFrom(env: Readonly<Record<string, unknown>>): SiteIdentity {
  return Object.freeze({
    organizationLegalName:
      configured(env["VITE_ORGANIZATION_LEGAL_NAME"]) ?? DEFAULT_ORGANIZATION_LEGAL_NAME,
    productName: configured(env["VITE_PRODUCT_NAME"]) ?? DEFAULT_PRODUCT_NAME,
    supportContact: configured(env["VITE_SUPPORT_CONTACT"]),
  });
}

/** This build's identity. */
export const SITE: SiteIdentity = siteIdentityFrom(import.meta.env);

/**
 * A context rather than a module constant read directly, so a test can render every page under a
 * different entity name and prove no page has the name baked in.
 */
export const SiteContext = createContext<SiteIdentity>(SITE);

export function useSite(): SiteIdentity {
  return useContext(SiteContext);
}

/** "October 1, 2026", from the ISO date, without the reader's time zone moving it a day. */
export function formatLegalDate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** A support contact as a link target: `mailto:` for an address, `tel:` for a number. */
export function supportHref(contact: string): string | null {
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact)) return `mailto:${contact}`;
  const digits = contact.replace(/[^\d+]/g, "");
  return digits.length >= 7 ? `tel:${digits}` : null;
}
